/**
 * Access-key hooks — list + create/enable-disable/delete. Every hook is scoped
 * by `serviceId` (the connection + credential is resolved server-side from it),
 * so all query keys carry it to keep caches isolated per service.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  unwrap,
  storageBase,
  s3Keys,
  type AccessKey,
  type AccessKeyStatus,
  type NewAccessKey,
} from './_shared';

/** List the service's S3 access keys. */
export function useAccessKeys(serviceId: string | undefined) {
  return useQuery<AccessKey[]>({
    queryKey: s3Keys.accessKeys(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${storageBase(serviceId!)}/access-keys`);
      const data = unwrap<{ access_keys?: AccessKey[] }>(res);
      return data?.access_keys ?? [];
    },
  });
}

/** Create a new access key. The secret is only in this response — surface it once. */
export function useCreateAccessKey(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<NewAccessKey, Error, void>({
    mutationFn: async () => {
      const res = await serviceApiV3.post(`${storageBase(serviceId)}/access-keys`);
      return unwrap<NewAccessKey>(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.accessKeys(serviceId) });
    },
  });
}

/** Enable/disable a key without deleting it. */
export function useUpdateAccessKeyStatus(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { accessKeyId: string; status: AccessKeyStatus }>({
    mutationFn: async ({ accessKeyId, status }) => {
      await serviceApiV3.patch(
        `${storageBase(serviceId)}/access-keys/${accessKeyId}`,
        { status },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.accessKeys(serviceId) });
    },
  });
}

/** Permanently delete a key — apps using it lose access immediately. */
export function useDeleteAccessKey(serviceId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (accessKeyId) => {
      await serviceApiV3.delete(`${storageBase(serviceId)}/access-keys/${accessKeyId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.accessKeys(serviceId) });
    },
  });
}
